import { DECK_ATTRIBUTE_NAME, DEFAULT_TEMPLATE_HEIGHT, DEFAULT_TEMPLATE_WIDTH, DIRECTIVES_SHEET_NAME, QUANTITY_ATTRIBUTE_NAME } from './constants.ts';
import { SpreadsheetData } from './google-docs-api.ts';
import { areNormalizedStringsEqual, isColor, normalizeString } from './utils.ts';
import { FontSize, Box } from './types.ts';
import { Project } from './project.ts';
import { CardData } from './project-types.ts';
import { BoardData, BoardZoneMetadata } from './board/board-types.ts';

export function parseSpreadsheet(spreadsheet: SpreadsheetData): Project {
    let project = new Project(spreadsheet.id, spreadsheet.title);

    parseDirectives(spreadsheet, project);
    parseBoard(spreadsheet, project);
    parseCards(spreadsheet, project);

    return project;
}

export function parseDirectives(spreadsheet: SpreadsheetData, project: Project) {
    let directivesSheet = spreadsheet.sheets.find(sheet => sheet.name === DIRECTIVES_SHEET_NAME);

    if (!directivesSheet) {
        return;
    }

    let lastRow: string[] = [];
    let columnCount = 3;

    for (let row of directivesSheet.rows) {
        for (let i = 0; i < columnCount; ++i) {
            if (!row[i]) {
                row[i] = lastRow[i];
            } else {
                break;
            }
        }

        for (let i = 0; i < columnCount; ++i) {
            row[i] ??= '';
        }

        let [keyword, source, target] = row;

        if (!keyword || keyword === '-') {
            continue;
        }

        lastRow = row;

        if (keyword === 'cards') {
            project.cardSheets.push(source);
        } else if (keyword === 'board') {
            project.boardSheets.push(source);
        } else if (keyword === 'font') {
            let list = source.split(',').map(item => item.trim());

            for (let item of list) {
                if (isColor(item)) {
                    project.font.color = item;
                } else {
                    project.font.name = item;
                }
            }
        } else if (keyword === 'panel') {
            let rect = parseRect(source);
            let content = target;

            project.panels.push({ rect, content });
        } else if (keyword.match(/map\(\w+\)/)) {
            let id = keyword.substring(keyword.indexOf('(') + 1, keyword.indexOf(')'));

            if (!project.mappings[id]) {
                project.mappings[id] = {};
            }

            project.mappings[id][source] = target;
        } else if (keyword === 'replace') {
            project.replacements[source] = target;
        }
    }
}

export function parseCards(spreadsheet: SpreadsheetData, project: Project) {
    for (let sheet of spreadsheet.sheets) {
        if (!project.cardSheets.some(name => areNormalizedStringsEqual(name, sheet.name))) {
            continue;
        }

        let attributes = (sheet.rows[0] || []).map(normalizeString);
        let quantityIndex = attributes.indexOf(QUANTITY_ATTRIBUTE_NAME);

        for (let i = 1; i < sheet.rows.length; ++i) {
            let row = sheet.rows[i];
            let quantity = parseQuantity(row, quantityIndex);

            if (quantity <= 0) {
                continue;
            }

            let cardNumber = i + 1;
            let cardId = `${normalizeString(sheet.name)}_${cardNumber}`;

            let card: CardData = {
                id: cardId,
                number: cardNumber,
                quantity,
                columns: {},
                image: null,
                imageId: null,
                deckIds: []
            };

            for (let i = 0; i < row.length; ++i) {
                let key = attributes[i];
                let value = row[i] ?? '';

                if (key === QUANTITY_ATTRIBUTE_NAME || !key) {
                    continue;
                }

                value = value.replaceAll('\n', '\\n');

                card.columns[key] = value;

                if (key === DECK_ATTRIBUTE_NAME && quantity > 0) {
                    let deckNames = value.split(',').map(str => str.trim());

                    for (let deckName of deckNames) {
                        let deck = project.getOrCreateDeck(deckName);

                        deck.cardIds.push(cardId);
                    }
                }
            }

            project.cards.push(card);
        }
    }
}

function parseQuantity(row: string[], quantityIndex: number) {
    if (quantityIndex === -1) {
        return 1;
    }

    let value = row[quantityIndex];

    if (!value) {
        return 0;
    }

    let quantity = parseInt(value);

    if (isNaN(quantity)) {
        return 1;
    }

    return quantity;
}

function parseRect(content: string = '', defaultRect: Partial<Box> = {}): Box {
    let fragments = content.split(',');
    let x = defaultRect.x ?? 0;
    let y = defaultRect.y ?? 0;
    let width = defaultRect.width ?? DEFAULT_TEMPLATE_WIDTH;
    let height = defaultRect.height ?? DEFAULT_TEMPLATE_HEIGHT;

    if (fragments.length === 2) {
        width = parseNumber(fragments[0], width);
        height = parseNumber(fragments[1], height);
    } else if (fragments.length >= 4) {
        x = parseNumber(fragments[0], x);
        y = parseNumber(fragments[1], y);
        width = parseNumber(fragments[2], width);
        height = parseNumber(fragments[3], height);
    }

    return { x, y, width, height };
}

function parseNumber(fragment: string, defaultValue: number): number {
    let value = parseFloat(fragment.trim());

    if (isNaN(value)) {
        return defaultValue;
    } else {
        return value;
    }
}

function isFontSize(content: string): content is FontSize {
    return /^\d+(px|%)$/.test(content);
}

export function parseBoard(spreadsheet: SpreadsheetData, project: Project) {
    for (let sheet of spreadsheet.sheets) {
        if (!project.boardSheets.some(name => areNormalizedStringsEqual(name, sheet.name))) {
            continue;
        }

        let board: BoardData = {
            rows: []
        };

        for (let line of sheet.rows) {
            if (line.length === 0) {
                break;
            }

            let row: BoardZoneMetadata[] = [];
            let lastZone: BoardZoneMetadata | null = null;

            for (let cell of line) {
                if (!cell || cell === '-') {
                    if (lastZone) {
                        lastZone.size += 1;
                    }
                } else {
                    let label = cell;
                    let options: string[] = [];
                    let match = cell.match(/([^()]+)\s*\(((?:\w+\s*,\s*)*\w+)\)/);

                    if (match) {
                        label = match[1].trim();
                        options = match[2].split(',').map(str => str.trim());
                    }

                    lastZone = {
                        label,
                        size: 1,
                        kind: 'stack',
                        autoFlip: null,
                        visibility: 'public',
                        isDeck: false,
                    };

                    for (let option of options) {
                        if (option === 'back') {
                            lastZone.autoFlip = 'back';
                        } else if (option === 'front') {
                            lastZone.autoFlip = 'front';
                        } else if (option === 'private') {
                            lastZone.visibility = 'private';
                        } else if (option === 'deck') {
                            lastZone.isDeck = true;
                            lastZone.autoFlip = 'back';
                        }
                    }

                    row.push(lastZone);
                }
            }

            board.rows.push(row);

            for (let zone of row) {
                zone.kind = zone.size === 1 ? 'stack' : 'spread';
            }
        }

        project.board = board;
    }
}